import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import SeoHead from '../components/SeoHead';
import {
  VEHICLES,
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  getVehiclePrice,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import { getRelatedRouteLinks } from '../data/routeLanding';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import { getRouteMetrics } from '../services/routeMetrics';
import '../styles/RouteLanding.css';

const BOOKING_ID = 'route-booking';

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const RouteLandingPage = ({ page }) => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [bookingRoute, setBookingRoute] = useState(null);
  const [routeMetrics, setRouteMetrics] = useState(null);
  const relatedLinks = useMemo(() => getRelatedRouteLinks(page), [page]);
  const liveDistanceKm = routeMetrics?.distanceKm || null;

  useEffect(() => {
    setPreferredVehicle('');
    setBookingRoute(null);
    setRouteMetrics(null);

    let cancelled = false;
    const loadMetrics = async () => {
      const metrics = await getRouteMetrics(page.origin, page.destination);
      if (!cancelled && metrics) setRouteMetrics(metrics);
    };

    if (window.google) {
      loadMetrics();
    } else {
      window.addEventListener('google-maps-ready', loadMetrics, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', loadMetrics);
    };
  }, [page.origin, page.destination, page.slug]);

  const jsonLd = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://easydroptaxi.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.title,
          item: `https://easydroptaxi.com${page.slug}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.title,
      serviceType: 'One-way taxi booking',
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        url: 'https://easydroptaxi.com/',
        telephone: '+919629954544',
      },
      areaServed: [page.displayOrigin, page.displayDestination, 'Tamil Nadu'],
      url: `https://easydroptaxi.com${page.slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ]), [page]);

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  return (
    <main className="route-landing">
      <SeoHead
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.slug}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>{page.title}</span>
            </nav>
            <h1>{page.h1}</h1>
            <p className="rl-hero-lead">{page.heroLead}</p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span><i className="fa fa-phone" aria-hidden="true"></i><a href="tel:+919629954544">+91 96299 54544</a></span>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              key={page.slug}
              initialPickup={bookingRoute?.pickup || page.origin}
              initialDrop={bookingRoute?.drop || page.destination}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="Route information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">Route</span>
              <span className="rl-strip-value">{page.displayOrigin} → {page.displayDestination}</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip Type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Distance</span>
              <span className="rl-strip-value">
                {liveDistanceKm
                  ? `${liveDistanceKm} km`
                  : 'Shown after fare estimate'}
              </span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Travel Time</span>
              <span className="rl-strip-value">
                {routeMetrics?.durationText || 'Calculated for your pickup'}
              </span>
            </div>
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={page.displayOrigin || page.origin}
        destination={page.displayDestination || page.destination}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setBookingRoute({ pickup: origin, drop: destination });
          setPreferredVehicle(vehicleType);
          scrollToBooking();
        }}
      />

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Choose Your Taxi</h2>
          <p className="rl-section-intro">
            Select from the same Sedan, SUV and Crysta fleet used across Easy Drop Taxi. Rates follow the published per-kilometre tariff, not a separate {page.displayDestination} package.
          </p>
          <div className="rl-vehicle-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-vehicle-card">
                <div className="rl-vehicle-media">
                  <img
                    src={vehicle.fleetImage || vehicle.image}
                    alt={`${getVehicleDisplayName(vehicle)} taxi for travel from ${page.displayOrigin} toward ${page.displayDestination}`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="rl-vehicle-body">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>{vehicle.description}</p>
                  <div className="rl-vehicle-meta">
                    <span><i className="fa fa-users" aria-hidden="true"></i>{vehicle.seats}</span>
                    <span><i className="fa fa-tag" aria-hidden="true"></i>₹{vehicle.rate}/km</span>
                  </div>
                  {liveDistanceKm ? (
                    <p>
                      Estimated one-way fare: ₹
                      {getVehiclePrice(liveDistanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                    </p>
                  ) : (
                    <p>Use Get Fare Estimate in the booking card for your exact total.</p>
                  )}
                  <button
                    type="button"
                    className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                    onClick={() => handleSelectVehicle(vehicle.type)}
                  >
                    {preferredVehicle === vehicle.type ? 'Selected' : 'Select'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>{page.title}</h2>
          <div className="rl-prose">
            {page.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Why Choose EasyDropTaxi</h2>
          <div className="rl-benefit-grid">
            <article className="rl-benefit-card">
              <i className="fa fa-laptop" aria-hidden="true"></i>
              <h3>Easy Online Booking</h3>
              <p>{page.displayOrigin} pickup and {page.displayDestination} drop are pre-filled. You still complete the rest of the trip in the same homepage booking form.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-exchange" aria-hidden="true"></i>
              <h3>One-Way Taxi Service</h3>
              <p>Travel from {page.displayOrigin} without paying for an unused return, or switch the form to Round Trip if you need both legs.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-calculator" aria-hidden="true"></i>
              <h3>Transparent Fare Calculation</h3>
              <p>The estimate uses billed distance, the published vehicle rate and driver bata, with extras listed when you review.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-car" aria-hidden="true"></i>
              <h3>Multiple Vehicle Options</h3>
              <p>Sedan, SUV and Crysta are offered at the existing tariff so you can match seats to your group.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-map-marker" aria-hidden="true"></i>
              <h3>Door-to-Door Pickup &amp; Drop</h3>
              <p>Start from a {page.displayOrigin} address and finish at a {page.displayDestination} address you type yourself, rather than a single stand.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-phone" aria-hidden="true"></i>
              <h3>Customer Support</h3>
              <p>Call <a href="tel:+919629954544">+91 96299 54544</a> if you need help before you confirm. Support is listed as 24/7 on the main site.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-user" aria-hidden="true"></i>
              <h3>Experienced Drivers</h3>
              <p>Outstation trips are assigned through Easy Drop Taxi’s existing driver network, not a separate operator for this route.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-snowflake-o" aria-hidden="true"></i>
              <h3>Fully A/C Taxis</h3>
              <p>
                The website fleet is listed as air-conditioned, which matters on this {liveDistanceKm ? `${liveDistanceKm} km ` : ''}intercity run.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Pickup and Drop on This Route</h2>
          <p className="rl-section-intro">
            Specific neighbourhood lists are shown only when the site already supports them. This route uses the city names in the booking fields instead of invented locality coverage.
          </p>
          <div className="rl-prose">
            <p>
              {page.originInCityList
                ? `${page.displayOrigin} is in Easy Drop Taxi’s existing city list. You can still type a more specific start — a home, hotel or another mapped point — in the pickup field.`
                : `${page.displayOrigin} is the supplied route label for pickup. Type that name or a more specific address; Google Places suggestions can complete the field. This is not a separate verified neighbourhood catalogue.`}
            </p>
            <p>
              {page.destinationInCityList
                ? `${page.displayDestination} is also in the existing city list. Drop-off stays editable if you need an airport, hotel or another point.`
                : `Drop defaults to ${page.destination}. You can enter a more specific ${page.displayDestination} address before you estimate. Neighbourhoods are not listed here unless they already exist as business data.`}
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>How to Book</h2>
          <div className="rl-step-grid">
            <article className="rl-step-card">
              <span className="rl-step-number">1</span>
              <h3>Enter Your Trip Details</h3>
              <p>Keep or edit {page.displayOrigin} pickup and {page.displayDestination} drop, add your 10-digit mobile number, then tap Get Fare Estimate.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">2</span>
              <h3>Select Your Vehicle</h3>
              <p>Choose Sedan, SUV or Crysta, then add the date and time you want to leave {page.displayOrigin} and your name.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">3</span>
              <h3>Confirm Your Booking</h3>
              <p>Review the trip and fare, then confirm. The request uses the existing Easy Drop Taxi booking system.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Fare &amp; Pricing</h2>
          <p className="rl-section-intro">
            There is no fixed {page.displayOrigin}–{page.displayDestination} package price. Totals use Easy Drop Taxi’s existing formula on the live mapped distance for this pickup and drop. If mapping is unavailable, use Get Fare Estimate in the booking card instead of a catalogue kilometre.
          </p>
          <div className="rl-fare-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-fare-card">
                <h3>{getVehicleDisplayName(vehicle)}</h3>
                <p>₹{vehicle.rate} per km</p>
                <p>Driver bata: ₹{DRIVER_BATA}</p>
                {liveDistanceKm ? (
                  <p>
                    Sample one-way estimate: ₹
                    {getVehiclePrice(liveDistanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                  </p>
                ) : (
                  <p>Open the booking card to generate a live estimate for this route.</p>
                )}
              </article>
            ))}
          </div>
          <p className="rl-fare-note">
            One-way billing uses a {MIN_ONEWAY_KM} km minimum. Toll gate, state permit, hill station, parking and waiting time are extra, matching the note shown when you review a booking.
          </p>
          <button type="button" className="rl-select-btn" style={{ maxWidth: 280 }} onClick={scrollToBooking}>
            Get Fare Estimate
          </button>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Travel from {page.displayOrigin} to {page.displayDestination}</h2>
          <div className="rl-prose">
            {page.travel.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {page.faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Related Pages</h2>
          <p className="rl-section-intro">
            Looking for a taxi from {page.displayDestination} to {page.displayOrigin}?{' '}
            <Link to={page.reverseSlug}>
              {page.displayDestination} to {page.displayOrigin} Taxi
            </Link>
          </p>
          <div className="rl-links">
            {relatedLinks.map((link) => (
              <Link key={link.path} to={link.path}>{link.label}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>{page.ctaTitle}</h2>
          <p>{page.ctaBody}</p>
          <button type="button" className="rl-cta-btn" onClick={scrollToBooking}>
            Book Your Taxi
          </button>
        </div>
      </section>
    </main>
  );
};

export default RouteLandingPage;
